import { useState, useMemo } from "react";
import {
  type ColumnDef,
  type SortingState,
  type VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { AlertCircle, CheckCircle, Clock, Columns, User } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { PriorityIndicator } from "@/components/compliance/PriorityIndicator";
import { TaskStatusBadge } from "@/components/compliance/TaskStatusBadge";
import type { ComplianceTask } from "@/hooks/use-compliance-tasks";
import { formatFramework } from "@/hooks/use-compliance-programs";

interface TaskDataTableProps {
  tasks: ComplianceTask[];
  searchTerm: string;
  updateTask: (taskId: string, updates: Partial<ComplianceTask>) => Promise<unknown>;
  onRowClick: (task: ComplianceTask) => void;
  getDepartmentColor: (id: string | null) => string;
  getDepartmentName: (id: string | null) => string;
}

function getInitials(name: string) {
  return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
}

function isOverdue(task: ComplianceTask) {
  if (!task.due_date || task.status === "complete") return false;
  return new Date(task.due_date) < new Date();
}

function getDaysUntilDue(due: string | null) {
  if (!due) return null;
  return Math.ceil((new Date(due).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
}

export function TaskDataTable({ tasks, searchTerm, updateTask, onRowClick, getDepartmentColor, getDepartmentName }: TaskDataTableProps) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

  const columns = useMemo<ColumnDef<ComplianceTask>[]>(
    () => [
      {
        id: "completion",
        header: "",
        cell: ({ row }) => (
          <CheckCircle className={`h-4 w-4 ${row.original.status === "complete" ? "text-green-600" : "text-gray-300"}`} />
        ),
        enableSorting: false,
        enableHiding: false,
        size: 40,
      },
      {
        accessorKey: "title",
        header: "Title",
        cell: ({ row }) => (
          <div className="font-medium max-w-xs">
            <div className="truncate">{row.original.title}</div>
            {row.original.description && (
              <div className="text-xs text-muted-foreground truncate">{row.original.description}</div>
            )}
          </div>
        ),
      },
      {
        accessorKey: "priority",
        header: "Priority",
        cell: ({ row }) => <PriorityIndicator priority={row.original.priority} />,
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => (
          <div onClick={(e) => e.stopPropagation()}>
            <Select
              value={row.original.status}
              onValueChange={(val) =>
                updateTask(row.original.id, { status: val as ComplianceTask["status"] })
              }
            >
              <SelectTrigger className="h-7 w-36 text-xs border-0 shadow-none p-1 focus:ring-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="not_started">Not Started</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="at_risk">At Risk</SelectItem>
                <SelectItem value="blocked">Blocked</SelectItem>
                <SelectItem value="complete">Complete</SelectItem>
              </SelectContent>
            </Select>
          </div>
        ),
        enableSorting: false,
      },
      {
        id: "assignee",
        header: "Assigned To",
        cell: ({ row }) =>
          row.original.primary_assignee ? (
            <div className="flex items-center gap-2">
              <Avatar className="h-6 w-6">
                <AvatarFallback className="text-xs">{getInitials(row.original.primary_assignee.name)}</AvatarFallback>
              </Avatar>
              <span className="text-sm">{row.original.primary_assignee.name}</span>
            </div>
          ) : (
            <span className="text-muted-foreground text-sm">Unassigned</span>
          ),
        enableSorting: false,
      },
      {
        id: "program",
        header: "Program",
        cell: ({ row }) =>
          row.original.program ? (
            <div className="space-y-1">
              <Badge variant="secondary" className="text-xs">
                {formatFramework(row.original.program.framework)}
              </Badge>
              {row.original.program_phase && (
                <div className="text-xs capitalize text-muted-foreground">{row.original.program_phase.replace("_", " ")}</div>
              )}
            </div>
          ) : (
            <span className="text-muted-foreground text-sm">—</span>
          ),
        enableSorting: false,
      },
      {
        id: "control",
        header: "Control",
        cell: ({ row }) =>
          row.original.control ? (
            <Badge variant="outline" className="text-xs">{row.original.control.control_id}</Badge>
          ) : (
            <span className="text-muted-foreground text-sm">—</span>
          ),
        enableSorting: false,
      },
      {
        id: "department",
        header: "Department",
        cell: ({ row }) => (
          <Badge
            variant="outline"
            className="text-xs"
            style={{
              backgroundColor: getDepartmentColor(row.original.department_id) + "15",
              borderColor: getDepartmentColor(row.original.department_id),
            }}
          >
            {getDepartmentName(row.original.department_id)}
          </Badge>
        ),
        enableSorting: false,
      },
      {
        accessorKey: "due_date",
        header: "Due Date",
        cell: ({ row }) => {
          if (!row.original.due_date) return <span className="text-muted-foreground text-sm">—</span>;
          const overdue = isOverdue(row.original);
          const days = getDaysUntilDue(row.original.due_date);
          return overdue ? (
            <span className="text-destructive text-sm flex items-center gap-1">
              <AlertCircle className="h-3 w-3" />Overdue
            </span>
          ) : days !== null && days <= 7 ? (
            <span className="text-orange-600 text-sm flex items-center gap-1">
              <Clock className="h-3 w-3" />{days}d
            </span>
          ) : (
            <span className="text-sm">{new Date(row.original.due_date).toLocaleDateString()}</span>
          );
        },
      },
    ],
    [getDepartmentColor, getDepartmentName, updateTask]
  );

  const table = useReactTable({
    data: tasks,
    columns,
    state: { sorting, columnVisibility, globalFilter: searchTerm },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: (row, _columnId, value) => {
      const search = String(value).toLowerCase();
      return (
        row.original.title.toLowerCase().includes(search) ||
        (row.original.description?.toLowerCase().includes(search) ?? false)
      );
    },
    getRowId: (row) => row.id,
  });

  return (
    <div className="space-y-2">
      <div className="flex justify-end">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-8 gap-1 text-xs">
              <Columns className="h-3.5 w-3.5" />
              Columns
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {table
              .getAllColumns()
              .filter((col) => col.getCanHide())
              .map((col) => (
                <DropdownMenuCheckboxItem
                  key={col.id}
                  className="capitalize"
                  checked={col.getIsVisible()}
                  onCheckedChange={(val) => col.toggleVisibility(!!val)}
                >
                  {col.id}
                </DropdownMenuCheckboxItem>
              ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id}>
                {hg.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className={cn(
                      header.column.getCanSort() ? "cursor-pointer select-none" : "",
                    )}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    <div className="flex items-center gap-1">
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getCanSort() && (
                        <span className="text-muted-foreground text-xs">
                          {{ asc: " ↑", desc: " ↓" }[header.column.getIsSorted() as string] ?? " ↕"}
                        </span>
                      )}
                    </div>
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="text-center py-10 text-muted-foreground">
                  No tasks found.
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => onRowClick(row.original)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
